import React, { useEffect, useState } from "react"
import { apiFetch } from "./apiClient"

const HERO_WORDS = ["Smarter", "Faster", "Autonomously", "24/7"]

function MainSite({ onNavigate, user, onLogout }) {
  const [backendText, setBackendText] = useState(
    "Empowering retail investors with institutional-grade market intelligence and automated trading capabilities."
  )
  const userInitial = user?.displayName?.[0]?.toUpperCase() || "N"
  const [displayText, setDisplayText] = useState("")
  const [wordIndex, setWordIndex] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)
  const [pause, setPause] = useState(false)

  const handleAnalyzeRequest = () => onNavigate && onNavigate("output")

  useEffect(() => {
    apiFetch("/startup-info")
      .then((res) => res.text())
      .then((data) => {
        setBackendText(data)
      })
      .catch(() => {
        console.log("Backend not connected, using default text")
      })
  }, [])

  useEffect(() => {
    if (pause) {
      const pauseTimer = setTimeout(() => setPause(false), 1200)
      return () => clearTimeout(pauseTimer)
    }

    const current = HERO_WORDS[wordIndex % HERO_WORDS.length]
    const delta = isDeleting ? 55 : 90

    const timer = setTimeout(() => {
      const nextText = isDeleting
        ? current.slice(0, Math.max(displayText.length - 1, 0))
        : current.slice(0, displayText.length + 1)
      setDisplayText(nextText)

      if (!isDeleting && nextText === current) {
        setPause(true)
        setIsDeleting(true)
      } else if (isDeleting && nextText === "") {
        setIsDeleting(false)
        setWordIndex((prev) => (prev + 1) % HERO_WORDS.length)
      }
    }, delta)

    return () => clearTimeout(timer)
  }, [displayText, isDeleting, wordIndex, pause])

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Navigation */}
      <nav className="relative z-10 px-6 py-4 border-b border-purple-100 bg-white/90 backdrop-blur">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-700 via-fuchsia-600 to-purple-500">
            Neo
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex space-x-8 text-sm font-medium">
              <a href="#features" className="text-purple-700 hover:text-purple-900 transition-colors">
                Features
              </a>
              <a href="#about" className="text-purple-700 hover:text-purple-900 transition-colors">
                About
              </a>
              <a href="#contact" className="text-purple-700 hover:text-purple-900 transition-colors">
                Contact
              </a>
              <button
                onClick={() => onNavigate && onNavigate("portfolio")}
                className="text-purple-700 hover:text-purple-900 transition-colors"
              >
                Portfolio
              </button>
            </div>
            {user && (
              <div className="flex items-center gap-2 bg-white border border-purple-100 rounded-full px-3 py-1 shadow-sm">
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || "User"}
                    className="w-8 h-8 rounded-full border border-purple-200"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white font-bold flex items-center justify-center">
                    {userInitial}
                  </div>
                )}
                <div className="hidden sm:block">
                  <p className="text-xs text-purple-500 leading-none">Signed in</p>
                  <p className="text-sm text-slate-900 font-semibold leading-none truncate max-w-[120px]">
                    {user.displayName || user.email}
                  </p>
                </div>
              </div>
            )}
            <button
              onClick={() => onNavigate && onNavigate("portfolio")}
              className="bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white px-4 py-2 rounded-lg hover:shadow-lg hover:shadow-purple-200 transition"
            >
              Portfolio
            </button>
            <button
              onClick={handleAnalyzeRequest}
              className="border border-purple-200 text-purple-700 px-4 py-2 rounded-lg hover:bg-purple-50 transition-colors"
            >
              Generate AI Insight
            </button>
            <button
              onClick={() => onNavigate && onNavigate("bubble")}
              className="bg-white border border-purple-200 text-purple-700 px-6 py-2 rounded-lg hover:bg-purple-50 transition-colors"
            >
              Get Started
            </button>
            {onLogout && (
              <button
                onClick={onLogout}
                className="bg-white border border-purple-100 text-purple-700 px-4 py-2 rounded-lg hover:shadow-md transition"
              >
                Log out
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative px-6 py-20">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-8">
            <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-6 leading-tight">
              Investing, Done
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-fuchsia-500">
                {displayText}
              </span>
            </h1>
            <p className="text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed">{backendText}</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <button
              onClick={() => onNavigate && onNavigate("bubble")}
              className="bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105 flex items-center justify-center shadow-lg shadow-purple-200"
            >
              Start Trading Smarter
              <span className="ml-2">→</span>
            </button>
            <button className="border-2 border-purple-200 text-purple-700 hover:bg-purple-50 px-8 py-4 rounded-lg font-semibold text-lg transition-all">
              Watch Demo
            </button>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section id="about" className="px-6 py-20 bg-purple-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">The Problem is Clear</h2>
            <p className="text-xl text-slate-700 max-w-4xl mx-auto">
              Everyday investors don't have the time or expertise to watch the markets and trade meaningfully. Let's change that.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white rounded-2xl shadow-sm border border-purple-100">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                📈
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Information Overload</h3>
              <p className="text-slate-600">Endless streams of financial data that's impossible to process manually</p>
            </div>
            <div className="text-center p-6 bg-white rounded-2xl shadow-sm border border-purple-100">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                ⏰
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">24/7 Markets</h3>
              <p className="text-slate-600">Markets never sleep, but you need to - missing crucial opportunities</p>
            </div>
            <div className="text-center p-6 bg-white rounded-2xl shadow-sm border border-purple-100">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                👥
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Unequal Access</h3>
              <p className="text-slate-600">Institutional advantages that individual investors simply can't match</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section id="features" className="px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">Meet Your AI Investment Team</h2>
            <p className="text-xl text-slate-700 max-w-4xl mx-auto">
              Neo levels the playing field with an agentic AI system that monitors markets, analyzes data,
              and makes intelligent decisions - all while you can't.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 text-xl">
                    🧠
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">24/7 Market Intelligence</h3>
                    <p className="text-slate-600">
                      AI agents continuously monitor financial news, technicals, and fundamentals to alert you of important
                      developments instantly.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 text-xl">
                    🛡️
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Automated Investing</h3>
                    <p className="text-slate-600">
                      Let our AI make intelligent investment decisions for you, removing emotion and human error from the equation.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 text-xl">
                    📊
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Intuitive Visualization</h3>
                    <p className="text-slate-600">
                      Our market visualization experience makes complex data easy to understand, perfect for beginners and experts
                      alike.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl p-8 border border-purple-100 shadow-lg">
                <div className="text-center">
                  <div className="w-24 h-24 bg-gradient-to-r from-purple-500 to-fuchsia-500 rounded-full mx-auto mb-6 flex items-center justify-center text-4xl">
                    🤖
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Your AI Investment Team</h3>
                  <p className="text-slate-700 mb-6">Working around the clock to maximize your returns</p>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex justify-between">
                      <span>Market Analysis</span>
                      <span className="text-green-600">●</span>
                    </div>
                    <div className="flex justify-between">
                      <span>News Monitoring</span>
                      <span className="text-green-600">●</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Risk Assessment</span>
                      <span className="text-green-600">●</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Trade Execution</span>
                      <span className="text-green-600">●</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20 bg-gradient-to-r from-purple-50 to-fuchsia-50 border-t border-purple-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">Ready to Transform Your Investing?</h2>
          <p className="text-xl text-slate-700 mb-8">
            Join the future of intelligent investing. Get started with Neo today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => onNavigate && onNavigate("bubble")}
              className="bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105 shadow-lg shadow-purple-200"
            >
              Try the beta version 
            </button>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 bg-purple-50 border-t border-purple-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="col-span-2">
              <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-fuchsia-500 mb-4">
                Neo
              </div>
              <p className="text-slate-600 max-w-md">
                Revolutionizing investment intelligence with AI-powered market analysis and automated trading solutions.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-4">Product</h4>
              <ul className="space-y-2 text-slate-600">
                <li>
                  <a href="#features" className="hover:text-purple-700 transition-colors">
                    Features
                  </a>
                </li>
                <li>
                  <button type="button" className="hover:text-purple-700 transition-colors">
                    Pricing
                  </button>
                </li>
                <li>
                  <button type="button" className="hover:text-purple-700 transition-colors">
                    API
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-4">Company</h4>
              <ul className="space-y-2 text-slate-600">
                <li>
                  <a href="#about" className="hover:text-purple-700 transition-colors">
                    About
                  </a>
                </li>
                <li>
                  <a href="#contact" className="hover:text-purple-700 transition-colors">
                    Contact
                  </a>
                </li>
                <li>
                  <button type="button" className="hover:text-purple-700 transition-colors">
                    Privacy
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-purple-100 mt-8 pt-8 text-center text-slate-500">
            <p>&copy; 2025 Neo. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default MainSite
